import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { finalize } from 'rxjs/operators';
import { IAppDataModel, IYearData, YearData } from '@app/@appstate/appdata.model';

import { AppDataQuery } from '@app/@appstate/appdata.query';
import { AppDataService } from '@app/@appstate/appdata.service';
import { AuthQuery } from '@app/auth/auth.query';

import { ConfirmationService } from 'primeng/api';

import { MatSnackBar } from '@angular/material/snack-bar';

import { JournalService } from '@app/@core';
import { SettingsService, Settings, SystemInfo } from '@app/services/settings.service';

import { environment } from '@env/environment';

@Component({
  selector: 'app-settings',
  templateUrl: './settings.component.html',
  styleUrls: ['./settings.component.css'],
})
export class SettingsComponent implements OnInit {
  settings: Settings;

  showBulkImport = environment.showBulkImport;
  showZahlungsabgleich = environment.showZahlungsabgleich;

  frmVSettings: FormGroup;

  // System info
  systemInfo: SystemInfo | null = null;
  loadingSystemInfo: boolean = false;

  // su toggle moved to Superuser page

  get isSuperuser(): boolean {
    return this.authQuery.isSuperuser;
  }

  readonly mobileParkingProviders = [
    { value: 'HandyParken', label: 'HandyParken' },
    { value: 'SmartParking', label: 'SmartParking' },
    { value: 'EasyPark', label: 'EasyPark' },
  ];

  // VSTV Freitexte textarea refs and placeholders
  @ViewChild('vorTextarea') vorTextarea: ElementRef<HTMLTextAreaElement>;
  @ViewChild('nachTextarea') nachTextarea: ElementRef<HTMLTextAreaElement>;
  settingsPlaceholders: string[] = [
    '##Aktenzahl##', '##Dienstnehmer_Name##', '##Dienst_Nummer##', '##Erfassungsdatum##',
    '##Anmerkungen##', '##Kennzeichen##', '##Marke##', '##Hersteller##',
    '##VSTV_Code##', '##Paragraph##',
    '##Tatdatum_von##', '##Tatdatum_bis##', '##Tatzeit_von##', '##Tatzeit_bis##',
    '##Anzahl_Fotos##', '##Strasse##', '##Hausnummer##',
  ];

  // Report logo (Berichte tab) - immediate preview after upload
  reportLogoPreviewUrl: string = '';
  reportLogoPondFiles: any[] = [];
  reportLogoPondOptions = {
    class: 'my-filepond',
    credits: false,
    multiple: false,
    allowFileTypeValidation: true,
    acceptedFileTypes: ['image/png', 'image/jpeg', 'image/gif', 'image/webp', 'image/svg+xml'],
    labelFileTypeNotAllowed: 'Ungültiger Dateityp: nur Bilder',
    fileValidateTypeLabelExpectedTypesMap: {
      'image/jpeg': '.jpg',
      'image/png': '.png',
      'image/gif': '.gif',
      'image/webp': '.webp',
      'image/svg+xml': '.svg',
    },
    labelIdle: 'Logo hier ablegen oder <a style="text-decoration:underline;">durchsuchen</a> ...',
    server: {
      process: (fieldName: string, file: any, metadata: any, load: any, error: any, progress: any, abort: any) => {
        const formData = new FormData();
        formData.append(fieldName, file, file.name);
        formData.append('filepond', file.name);

        const request = new XMLHttpRequest();
        request.open('POST', environment.serverUrl + '/settings-logo-upload');
        request.setRequestHeader('Access-Control-Allow-Origin', '*');
        request.setRequestHeader('Access-Control-Allow-Headers', '*');
        request.setRequestHeader('Authorization', `Bearer ${this.authQuery.access_token}`);

        request.upload.onprogress = (e) => {
          progress(e.lengthComputable, e.loaded, e.total);
        };

        request.onload = () => {
          if (request.status >= 200 && request.status < 300) {
            const storedPath = request.responseText;
            // persist the stored path into the settings form (saved with the rest)
            this.frmVSettings.patchValue({ REPORT_LOGO: storedPath });
            this.frmVSettings.markAsDirty();
            // immediate local preview of the just-selected file
            try {
              this.reportLogoPreviewUrl = URL.createObjectURL(file);
            } catch (e) {
              /* ignore */
            }
            load(storedPath);
          } else {
            error('upload failed');
          }
        };

        request.send(formData);

        return {
          abort: () => {
            request.abort();
            abort();
          },
        };
      },
      revert: null,
      restore: null,
      fetch: null,
      load: null,
    },
  };

  constructor(
    private _formBuilder: FormBuilder,
    private _adQuery: AppDataQuery,
    private _adService: AppDataService,
    private authQuery: AuthQuery,
    private confirmationService: ConfirmationService,
    private formBuilder: FormBuilder,
    private _snackBar: MatSnackBar,
    private journalService: JournalService,
    private settingsService: SettingsService
  ) {}

  ngOnInit(): void {
    // init the forms
    this.createForms();

    // get the stammdaten
    this.getStammdaten();

    // load system info
    this.loadSystemInfo();
  }

  /**
   * Load system information (server, DB, mail, VSTV, Dienstnehmer)
   */
  loadSystemInfo() {
    this.loadingSystemInfo = true;
    this.settingsService.getSystemInfo().subscribe(
      (data: SystemInfo) => {
        this.systemInfo = data;
        this.loadingSystemInfo = false;
      },
      (error) => {
        console.error('Failed to load system info', error);
        this.loadingSystemInfo = false;
      }
    );
  }

  /**
   * get the stammdaten
   */
  getStammdaten() {
    console.log('do transfer');

    /* BUG BUG BUG -- we need some kind of security
    let request = {
      api_key: this._config.getConfig().api_key,
      api_baseurl: this._config.getConfig().api_baseurl,
      company_id: this._config.getConfig().company_id,
      person_id: this._config.getConfig().person_id,
    };
    */

    let requestData = {
      api_key: new Date().getTime(),
      //api_baseurl: this._config.getConfig().api_baseurl,
      //company_id: this._config.getConfig().company_id,
      //person_id: this._config.getConfig().person_id,
      do: 'getStammdaten',
    };

    this.settingsService.getSettings().subscribe(
      (response: Settings) => {
        //next() callback
        console.log('response received');
        console.log(response);

        this.settings = response;
        this.frmVSettings.patchValue({
          ...this.settings,
          MOBILE_PARKING_ENABLED: this.coerceSettingsBool(this.settings.MOBILE_PARKING_ENABLED),
        });
        this.applyMobileParkingFormState();

        // set the date
      },
      (error) => {
        //error() callback
        console.error('Request failed with error');
        //this.errorMessage = error;
      },
      () => {
        //complete() callback
        console.log('Request completed'); //This is actually not needed
      }
    );
  }

  private createForms() {
    // BUG BUG BUG: load the current data from ?
    //
    this.frmVSettings = this.formBuilder.group({
      EMPFANGER: ['', Validators.required],
      SENDER: ['', Validators.required],
      GEMEINDE: ['', Validators.required],
      TATORTART: ['', Validators.required],
      LANDESKENNZEICHEN: ['', Validators.required],
      WAHRNEHMUNG: ['', Validators.required],
      VSTV_PRE_NUMMER: ['', Validators.required],
      UNTERZEICHNER: ['', Validators.required],
      BEARBEITER: ['', Validators.required],
      TAGE_ZUR_ANZEIGE: [0, Validators.required],
      TAGE_ZUR_BOM: [0, Validators.required],
      VOR_TEXTBAUSTEIN: [''],
      NACH_TEXTBAUSTEIN: [''],
      VSTV_BEWEISMITTEL_EXPORT: ['0'],
      MAIL_DEFAULT_TO: [''],
      MAIL_DEFAULT_BCC: [''],
      MAIL_DEFAULT_SUBJECT: [''],
      MAIL_DEFAULT_BODY: [''],
      WHITELIST_DEFAULT_REASON: [''],
      WHITELIST_NOTIFY_MOBILE: ['0'],
      WHITELIST_MOBILE_MESSAGE: [''],

      // Bulk-Import (Barcode) defaults
      BULK_STRAFE: [20],
      BULK_LAND: ['A'],
      BULK_BEZIRK: [''],
      BULK_ZONE: [0],
      BULK_FAHRZEUGART: [0],
      BULK_BEARBEITER: [''],

      // Report (Berichte) header/footer config
      REPORT_COMPANY_NAME: [''],
      REPORT_ADDRESS: [''],
      REPORT_IBAN: [''],
      REPORT_FOOTER_TEXT: [''],
      REPORT_LOGO: [''],
      REPORT_PRINT_DATE: [true],
      REPORT_PRINT_PAGENUMBERS: [true],
      REPORT_PRINT_TOTALSUM: [true],

      MOBILE_PARKING_ENABLED: [false],
      MOBILE_PARKING_PROVIDER: ['HandyParken'],
      MOBILE_PARKING_API_KEY: [''],
      MOBILE_PARKING_SECURITY_TOKEN: [''],
    });
  }

  private applyMobileParkingFormState(): void {
    const mobileKeys = [
      'MOBILE_PARKING_ENABLED',
      'MOBILE_PARKING_PROVIDER',
      'MOBILE_PARKING_API_KEY',
      'MOBILE_PARKING_SECURITY_TOKEN',
    ];
    mobileKeys.forEach((key) => {
      const control = this.frmVSettings.get(key);
      if (!control) {
        return;
      }
      if (this.isSuperuser) {
        control.enable({ emitEvent: false });
      } else {
        control.disable({ emitEvent: false });
      }
    });
  }

  private coerceSettingsBool(value: boolean | string | number | undefined): boolean {
    return value === true || value === 1 || value === '1';
  }

  /**
   * Remove the configured report logo (cleared on next save).
   */
  removeReportLogo() {
    this.frmVSettings.patchValue({ REPORT_LOGO: '' });
    this.frmVSettings.markAsDirty();
    this.reportLogoPreviewUrl = '';
  }

  /**
   * sync the settings to the server
   */
  saveSettings() {
    console.log('save  Settings');

    var username = this.authQuery.name;
    var userid = this.authQuery.userid;
    var token = this.authQuery.access_token;

    // Merge the loaded settings with the form values so DB-only keys that are
    // not present in the form (e.g. VSTV_Parkgesetz_*, SONSTIGER_STRASSENERHALTER)
    // are preserved - sync_settings replaces the whole settings JSON.
    let data = {
      username: username,
      uid: userid,
      token: token,
      data: { ...(this.settings || {}), ...this.frmVSettings.getRawValue() },
    };

    this.settingsService
      .syncSettings(data)
      .pipe(
        finalize(() => {
          this.frmVSettings.markAsPristine();
        })
      )
      .subscribe(
        (data) => {
          //console.log(data);

          this._snackBar.open('Änderungen erfolgreich durchgeführt', 'Schließen', {
            horizontalPosition: 'center',
            verticalPosition: 'top',
            duration: 3000,
            panelClass: 'awesome-snackbar',
          });
        },
        (error) => {
          // show error notification alert
          this._snackBar.open('Fehler aufgetreten', 'Schließen', {
            horizontalPosition: 'center',
            verticalPosition: 'top',
            duration: 3000,
          });
        }
      );
  }

  /**
   * Insert a selected placeholder into the VOR or NACH textarea at cursor position
   */
  insertSettingsPlaceholder(target: 'VOR' | 'NACH', event: Event) {
    const select = event.target as HTMLSelectElement;
    const placeholder = select.value;
    if (!placeholder) return;

    const formControlName = target === 'VOR' ? 'VOR_TEXTBAUSTEIN' : 'NACH_TEXTBAUSTEIN';
    const textareaRef = target === 'VOR' ? this.vorTextarea : this.nachTextarea;
    const textarea = textareaRef?.nativeElement;
    const currentValue = this.frmVSettings.get(formControlName)?.value || '';

    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const newValue = currentValue.substring(0, start) + placeholder + currentValue.substring(end);
      this.frmVSettings.patchValue({ [formControlName]: newValue });

      // Restore cursor after placeholder
      setTimeout(() => {
        textarea.focus();
        const newPos = start + placeholder.length;
        textarea.setSelectionRange(newPos, newPos);
      }, 0);
    } else {
      // Fallback: append
      this.frmVSettings.patchValue({ [formControlName]: currentValue + placeholder });
    }

    // Reset dropdown
    select.value = '';
  }

  saveMobileParkingSettings(): void {
    if (!this.isSuperuser) {
      this._snackBar.open('Nur Superuser dürfen Mobile-Parking-Einstellungen ändern.', 'Schließen', {
        horizontalPosition: 'center',
        verticalPosition: 'top',
        duration: 4000,
      });
      return;
    }
    this.saveSettings();
  }

  /** Prevent browser login/password autofill on Mobile Parking credential fields */
  enableMobileParkingField(event: Event): void {
    const el = event.target as HTMLInputElement;
    el.removeAttribute('readonly');
  }

  get mobileParkingEnabled(): boolean {
    return this.coerceSettingsBool(this.frmVSettings.get('MOBILE_PARKING_ENABLED')?.value);
  }
}
